import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pause, Play, Loader2, Calendar } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  loans: () => apiClient.get('/payroll/loans').then((r) => r.data),
  loan: (id) => apiClient.get(`/loans/${id}`).then((r) => r.data),
  schedule: (id) => apiClient.get(`/loans/${id}/schedule`).then((r) => r.data),
  pause: (id, p) => apiClient.patch(`/loans/${id}/pause`, p).then((r) => r.data),
  patch: (id, p) => apiClient.patch(`/loans/${id}`, p).then((r) => r.data),
  loanTypes: () => apiClient.get('/hr/masters/loan-types').then((r) => r.data),
  employees: () => apiClient.get('/hr/employees').then((r) => r.data),
};

const LoansListPage = () => {
  const [loans, setLoans] = useState([]);
  const [types, setTypes] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, t, e] = await Promise.all([api.loans(), api.loanTypes(), api.employees()]);
      setLoans(l.data || []); setTypes(t.data || []); setEmps(e.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const typeById = Object.fromEntries(types.map((t) => [t.id, t]));
  const empById = Object.fromEntries(emps.map((e) => [e.user_id, e]));
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Loans</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Loans & advances</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Click any loan to see its EMI schedule and pause/resume repayments.</p>
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Employee</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Type</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Principal</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">EMI</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Paid</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
            </tr></thead>
            <tbody>{loans.map((l) => (
              <tr key={l.id} className="border-t border-black/5 hover:bg-[#f7fbf8] cursor-pointer" onClick={() => nav(`/admin/loans/${l.id}`)} data-testid={`loan-row-${l.id}`}>
                <td className="p-2">{empById[l.user_id]?.name || l.user_id}</td>
                <td className="p-2 text-xs">{typeById[l.loan_type_id]?.name || '-'}</td>
                <td className="p-2 text-right">{l.principal.toFixed(2)}</td>
                <td className="p-2 text-right">{l.emi_amount.toFixed(2)}</td>
                <td className="p-2 text-right text-xs">{l.installments_paid || 0}/{l.tenure_months || '?'}</td>
                <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${l.paused ? 'bg-amber-100 text-amber-800' : l.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200'}`}>{l.paused ? 'paused' : l.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
          {loans.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No loans issued.</div>}
        </div>
      )}
    </div>
  );
};

export const LoanDetailPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [loan, setLoan] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [tenure, setTenure] = useState('');
  const load = useCallback(async () => {
    const [l, s] = await Promise.all([api.loan(id), api.schedule(id)]);
    setLoan(l); setSchedule(s);
    setTenure(l.tenure_months || '');
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!loan || !schedule) return <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div>;
  const togglePause = async () => {
    const reason = loan.paused ? null : window.prompt('Reason for pausing?') || '';
    try {
      const updated = await api.pause(id, { paused: !loan.paused, reason });
      setLoan({ ...loan, ...updated });
      toast({ title: updated.paused ? 'Paused' : 'Resumed' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const saveTenure = async () => {
    try {
      const updated = await api.patch(id, { tenure_months: parseInt(tenure, 10) });
      setLoan({ ...loan, ...updated });
      toast({ title: 'Tenure updated' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const s = schedule.summary;
  const paidPct = s.principal ? Math.round((s.paid_total / s.principal) * 100) : 0;
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="text-xs text-[#0f5c3d] hover:underline mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to loans</button>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Summary card */}
        <div className="lg:col-span-1 bg-white border border-black/5 rounded-2xl overflow-hidden">
          <div className="p-5 bg-gradient-to-br from-[#0f5c3d] to-[#0b1a2e] text-white">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Loan · {loan.loan_type?.name || 'General'}</div>
            <div className="text-2xl font-semibold mt-1">{loan.employee?.name || loan.user_id}</div>
            <div className="text-xs opacity-70 mt-1">{loan.employee?.employee_code || ''}</div>
          </div>
          <div className="p-5 space-y-3">
            <Row label="Principal"><b>{s.principal.toLocaleString()}</b></Row>
            <Row label="EMI">{s.emi.toLocaleString()}</Row>
            <div>
              <Row label="Tenure (months)">
                <div className="flex gap-1">
                  <input type="number" value={tenure} onChange={(e) => setTenure(e.target.value)} data-testid="tenure-input"
                    className="w-16 px-2 py-1 border rounded text-sm text-right" />
                  <button onClick={saveTenure} data-testid="tenure-save" className="px-2 py-1 rounded bg-[#0f5c3d] text-white text-[10px]">Save</button>
                </div>
              </Row>
            </div>
            <Row label="Installments paid"><span className="text-emerald-700 font-bold">{s.paid_installments}</span></Row>
            <Row label="Paid so far">{s.paid_total.toLocaleString()}</Row>
            <Row label="Outstanding"><b className="text-red-600">{s.outstanding.toLocaleString()}</b></Row>
            {loan.accrued_interest > 0 && <Row label="Accrued interest (paused)"><b className="text-amber-600">+{loan.accrued_interest.toLocaleString()}</b></Row>}
            <div>
              <div className="text-[10px] uppercase text-[#0b1a2e]/60 mb-1">Progress</div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#0f5c3d]" style={{ width: `${paidPct}%` }} />
              </div>
              <div className="text-[10px] text-[#0b1a2e]/60 mt-1">{paidPct}% repaid</div>
            </div>
            <button onClick={togglePause} data-testid="pause-toggle"
              className={`w-full mt-3 px-4 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 ${loan.paused ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
              {loan.paused ? <><Play className="w-4 h-4" /> Resume EMI</> : <><Pause className="w-4 h-4" /> Pause EMI</>}
            </button>
            {loan.pause_reason && <div className="text-[10px] text-[#0b1a2e]/60 italic">Paused: {loan.pause_reason}</div>}
          </div>
        </div>
        {/* Schedule */}
        <div className="lg:col-span-2 bg-white border border-black/5 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-[#f7fbf8] border-b border-black/5 font-semibold text-sm text-[#0b1a2e]"><Calendar className="w-4 h-4 inline mr-1" /> Amortisation schedule</div>
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white shadow-sm"><tr>
                <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60 w-16">#</th>
                <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Due</th>
                <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Outstanding</th>
                <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60 w-24">Status</th>
              </tr></thead>
              <tbody>{schedule.rows.map((r) => (
                <tr key={r.installment_no} className="border-t border-black/5" data-testid={`emi-row-${r.installment_no}`}>
                  <td className="p-2 font-mono text-xs">{r.installment_no}</td>
                  <td className="p-2 text-right">{r.due.toLocaleString()}</td>
                  <td className="p-2 text-right text-xs text-[#0b1a2e]/60">{r.outstanding_after.toLocaleString()}</td>
                  <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : r.status === 'current' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{r.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-[#0b1a2e]/60 text-xs">{label}</span>
    <span>{children}</span>
  </div>
);

export default LoansListPage;
